import {
  CLIENT_PARTY_AUTHORITY_BASES,
  type ClientGroupMember,
  type CreateClientGroupMemberRequest,
  MAX_CLIENT_REPRESENTATIVES,
  MAX_GROUP_MEMBERS,
} from "@repo/shared/types";
import { Link2, Link2Off, Pencil, Plus, Trash2, User } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchInput } from "@/components/ui/search-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useClientIdentity, VerifiedIdBadge, VerifiedIdDialog } from "@/components/verified-id";
import { useConfirm } from "@/hooks/use-confirm";
import { del, patch, post } from "@/lib/api/_client";
import { useClientGroupMembers, useClientParties } from "@/lib/hooks/use-client-detail";
import { useClients } from "@/lib/hooks/use-clients";
import { useDebouncedValue } from "@/lib/hooks/use-debounced-value";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./group-members-section.i18n";

// QP-184 — group composition card: members are either links to existing
// Person clients or inline quick-adds (max MAX_GROUP_MEMBERS).

/**
 * QL-130 — the fields a linked Person client contributes to the member row.
 * The member keeps its own snapshot of these (the link can be broken later, or
 * the client deleted), so they are copied across at link time.
 */
type LinkableClient = {
  id: string;
  type: string;
  name: string;
  ssn: string | null;
  dateOfBirth: string | null;
  email: string | null;
  mobilePhone: string | null;
};

/** API nulls and blank strings both mean "not set" in the form. */
const text = (value: string | null | undefined) => value?.trim() || "";

type MemberFormState = {
  memberClientId: string | null;
  name: string;
  ssn: string;
  dateOfBirth: string;
  email: string;
  phone: string;
  isRepresentative: boolean;
  authorityBasis: string;
};

const EMPTY_FORM: MemberFormState = {
  memberClientId: null,
  name: "",
  ssn: "",
  dateOfBirth: "",
  email: "",
  phone: "",
  isRepresentative: false,
  authorityBasis: "",
};

export function GroupMembersSection({
  clientId,
  autoEditMemberId,
  onAutoEditMemberConsumed,
}: {
  clientId: string;
  /** QL-133 — the member to open, set when arriving from the Representative tab's edit action. */
  autoEditMemberId?: string | null;
  onAutoEditMemberConsumed?: () => void;
}) {
  const t = useT(T);
  const { members, mutate } = useClientGroupMembers(clientId);
  // QL-131 — an external representative on the Representative tab takes
  // precedence over any member flag, so the flag is not offered while one
  // exists; a group answers through its registered representatives (QL-654: one or several).
  const { parties } = useClientParties(clientId);
  const externalRepresentative = (parties ?? []).length > 0;
  const { confirm, ConfirmationDialog } = useConfirm();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [identityOpen, setIdentityOpen] = useState(false);
  const { identity } = useClientIdentity(clientId);
  const [editing, setEditing] = useState<ClientGroupMember | null>(null);
  // The server enforces the cap; the flag is just not offered once it is reached
  // (the member being edited does not count against itself).
  const flagCapReached =
    members.filter((m) => m.isRepresentative && m.id !== editing?.id).length >=
    MAX_CLIENT_REPRESENTATIVES;
  const [form, setForm] = useState<MemberFormState>(EMPTY_FORM);
  const [search, setSearch] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // QL-136 — the clients list is paged on the server, so the picker asks the
  // server for Person clients matching the typed text instead of scanning a
  // full list in the browser.
  const debouncedSearch = useDebouncedValue(search);
  const { clients } = useClients({ search: debouncedSearch, type: "Person", pageSize: 20 });

  // QL-135 / QL-505 follow-up — a member can be linked to an existing client
  // at add time, or later while editing IF it is still inline (unlinked).
  // Once linked, the link can be removed (M10) but not repointed to a
  // different client from here.
  const alreadyLinked = !!editing?.memberClientId;
  const canLink = editing === null || editing.memberClientId === null;

  const linkedIds = useMemo(
    () => new Set(members.map((m) => m.memberClientId).filter(Boolean)),
    [members],
  );
  const linkableClients = useMemo(
    () =>
      (clients ?? [])
        .filter(
          (c: LinkableClient) =>
            c.id !== clientId && (!linkedIds.has(c.id) || c.id === editing?.memberClientId),
        )
        .slice(0, 8),
    [clients, clientId, linkedIds, editing],
  );

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setSearch("");
    setError(null);
    setDialogOpen(true);
  };

  const openEdit = (member: ClientGroupMember) => {
    setEditing(member);
    setForm({
      memberClientId: member.memberClientId,
      name: member.name,
      ssn: member.ssn ?? "",
      dateOfBirth: member.dateOfBirth ?? "",
      email: member.email ?? "",
      phone: member.phone ?? "",
      isRepresentative: member.isRepresentative,
      authorityBasis: member.authorityBasis ?? "",
    });
    setSearch("");
    setError(null);
    setDialogOpen(true);
  };

  // QL-133 — the Representative tab edits its acting members here, so opening
  // the member's dialog is this section's job; it owns the members list.
  // QL-654 — the member the pencil belongs to, not the first flagged one.
  useEffect(() => {
    if (!autoEditMemberId || dialogOpen) return;
    const member = members.find((m) => m.id === autoEditMemberId);
    if (!member) return;
    openEdit(member);
    onAutoEditMemberConsumed?.();
  }, [autoEditMemberId, dialogOpen, members, onAutoEditMemberConsumed]);

  // QL-143 — a member is savable once it has an identity (a linked client or a
  // typed name); Save stays disabled until then rather than opening the dialog
  // armed and answering the first click with "Name is required".
  const isValid = !!form.memberClientId || !!form.name.trim();

  // ...and on edit, until something actually differs from the stored member.
  const isDirty =
    !editing ||
    form.memberClientId !== editing.memberClientId ||
    form.name.trim() !== editing.name ||
    form.ssn.trim() !== (editing.ssn ?? "") ||
    form.dateOfBirth !== (editing.dateOfBirth ?? "") ||
    form.email.trim() !== (editing.email ?? "") ||
    form.phone.trim() !== (editing.phone ?? "") ||
    form.isRepresentative !== editing.isRepresentative ||
    // QL-134's authority basis is part of the member too, so a change to it
    // alone still counts as something worth saving.
    form.authorityBasis !== (editing.authorityBasis ?? "");

  const handleSave = async () => {
    if (!form.memberClientId && !form.name.trim()) {
      setError(t.nameRequired);
      return;
    }

    // QL-654 — several members may be marked representative; marking one no
    // longer moves the flag off another, so there is nothing to confirm.
    setSaving(true);
    setError(null);
    const payload: CreateClientGroupMemberRequest = {
      memberClientId: form.memberClientId,
      name: form.name.trim() || undefined,
      ssn: form.ssn.trim() || null,
      dateOfBirth: form.dateOfBirth || null,
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      isRepresentative: form.isRepresentative,
      authorityBasis: (form.isRepresentative && form.authorityBasis
        ? form.authorityBasis
        : null) as CreateClientGroupMemberRequest["authorityBasis"],
    };
    try {
      if (editing) {
        await patch(`/internal/client-group-members/${clientId}/${editing.id}`, payload);
      } else {
        await post(`/internal/client-group-members/${clientId}`, payload);
      }
      setDialogOpen(false);
      mutate();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.failedToSave);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (member: ClientGroupMember) => {
    const displayName = member.linkedClient?.name || member.name;
    const ok = await confirm({
      title: t.deleteMember,
      description: t.deleteConfirmation(displayName),
      confirmText: t.delete,
      cancelText: t.cancel,
      variant: "destructive",
    });
    if (!ok) return;
    await del(`/internal/client-group-members/${clientId}/${member.id}`);
    mutate();
  };

  const selectLinkedClient = (c: LinkableClient) => {
    setForm((prev) => ({
      ...prev,
      memberClientId: c.id,
      name: c.name,
      // The linked client's details replace whatever was there, blanks
      // included. Falling back to the previous values would let a client
      // linked earlier leave its SSN or email on a different person once the
      // link is swapped — these fields belong to whoever is linked now.
      ssn: text(c.ssn),
      dateOfBirth: text(c.dateOfBirth),
      email: text(c.email),
      phone: text(c.mobilePhone),
    }));
    setSearch("");
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1.5">
            <CardTitle>{t.title}</CardTitle>
            <CardDescription>{t.description}</CardDescription>
          </div>
          <Button
            size="sm"
            onClick={openAdd}
            disabled={members.length >= MAX_GROUP_MEMBERS}
            className="shrink-0 gap-2"
          >
            <Plus className="h-4 w-4" />
            {t.addMember} ({members.length}/{MAX_GROUP_MEMBERS})
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {members.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">{t.noMembers}</p>
        ) : (
          <div className="space-y-2">
            {members.map((member) => {
              const displayName = member.linkedClient?.name || member.name;
              const displayEmail = member.linkedClient?.email || member.email;
              const displayPhone = member.linkedClient?.mobilePhone || member.phone;
              return (
                <div
                  key={member.id}
                  className="flex items-center gap-3 p-3 border rounded-lg bg-muted/30"
                >
                  <User className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{displayName}</span>
                      {/* QL-e — only the member who ACTUALLY acts for the group
                          carries the badge. `identitySubjectFrom` falls back to
                          the flagged member only when no `client_party` exists,
                          so an external representative (QL-131) takes
                          precedence here too — otherwise the badge would credit
                          this member with a verification of somebody else. */}
                      {member.isRepresentative &&
                        !externalRepresentative &&
                        identity?.summary.subjectKind === "representative" && (
                          <VerifiedIdBadge
                            // QL-654 — several flagged members each have their own standing.
                            summary={
                              identity.summary.subjects
                                ? {
                                    ...identity.summary,
                                    verified:
                                      identity.summary.subjects.find(
                                        (s) => s.subjectId === member.id,
                                      )?.verified ?? false,
                                  }
                                : identity.summary
                            }
                            onOpen={() => setIdentityOpen(true)}
                          />
                        )}
                      {member.isRepresentative && (
                        <Badge variant="secondary">{t.representative}</Badge>
                      )}
                      {member.isRepresentative && member.authorityBasis && (
                        <Badge variant="outline">
                          {t.basisLabels[member.authorityBasis] ?? member.authorityBasis}
                        </Badge>
                      )}
                      {member.memberClientId ? (
                        member.linkedClientAccessible ? (
                          <Link
                            to={`/clients/${member.memberClientId}`}
                            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                          >
                            <Link2 className="h-3 w-3" />
                            {t.linkedTo}
                          </Link>
                        ) : (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                                <Link2 className="h-3 w-3" />
                                {t.linkedTo}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>{t.linkedNoAccess}</TooltipContent>
                          </Tooltip>
                        )
                      ) : (
                        // QL-505 follow-up — an inline member cannot be given a
                        // member link, so the advisor needs to see this at a
                        // glance rather than discover it inside the Invite dialog.
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                          <Link2Off className="h-3 w-3" />
                          {t.notLinked}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">
                      {[member.ssn, displayEmail, displayPhone].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    tooltip={t.editMember}
                    onClick={() => openEdit(member)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    tooltip={t.deleteMember}
                    onClick={() => handleDelete(member)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? t.editMember : t.addMember}</DialogTitle>
            <DialogDescription>
              {editing ? t.editDialogDescription : t.dialogDescription}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                {error}
              </div>
            )}

            {/* Link an existing person client — QL-135 / QL-505 follow-up:
                offered when adding, and when editing a still-inline member.
                An already-linked member (at dialog-open time) shows the
                linked client read-only — no repoint from here. */}
            {alreadyLinked ? (
              // M10 — unlinking keeps the member row (same id, so its answers
              // keep counting); only its personal link is withdrawn. The API
              // accepts memberClientId: null; repointing to a different client
              // is still not offered here.
              <div className="space-y-2">
                <Label>{t.linkedTo}</Label>
                <div className="flex items-center justify-between gap-2 p-2 border rounded-md">
                  <span className="inline-flex items-center gap-2 text-sm">
                    {form.memberClientId ? (
                      <Link2 className="h-4 w-4 text-primary" />
                    ) : (
                      <Link2Off className="h-4 w-4 text-muted-foreground" />
                    )}
                    {editing?.linkedClient?.name || editing?.name}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        memberClientId: prev.memberClientId
                          ? null
                          : (editing?.memberClientId ?? null),
                      }))
                    }
                  >
                    {form.memberClientId ? t.unlink : t.undoUnlink}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {form.memberClientId ? t.unlinkNote : t.unlinkPending}
                </p>
              </div>
            ) : (
              (canLink || form.memberClientId) && (
                <div className="space-y-2">
                  <Label>{t.linkExisting}</Label>
                  {form.memberClientId ? (
                    <div className="flex items-center justify-between gap-2 p-2 border rounded-md">
                      <span className="inline-flex items-center gap-2 text-sm">
                        <Link2 className="h-4 w-4 text-primary" />
                        {form.name}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setForm((prev) => ({ ...prev, memberClientId: null }))}
                      >
                        {t.unlink}
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <SearchInput
                        value={search}
                        onValueChange={setSearch}
                        placeholder={t.searchClients}
                      />
                      {search.trim() && (
                        <div className="border rounded-md divide-y max-h-48 overflow-y-auto">
                          {linkableClients.length === 0 ? (
                            <p className="text-sm text-muted-foreground p-2">{t.noMatches}</p>
                          ) : (
                            linkableClients.map((c: LinkableClient) => (
                              <button
                                key={c.id}
                                type="button"
                                onClick={() => selectLinkedClient(c)}
                                className="w-full text-left text-sm p-2 hover:bg-muted"
                              >
                                {c.name}
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2 col-span-2">
                <Label>
                  {t.name} <span className="text-destructive">*</span>
                </Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  disabled={!!form.memberClientId}
                />
              </div>
              <div className="space-y-2">
                <Label>{t.ssn}</Label>
                <Input
                  value={form.ssn}
                  onChange={(e) => setForm((prev) => ({ ...prev, ssn: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>{t.dateOfBirth}</Label>
                <DatePicker
                  value={form.dateOfBirth}
                  onChange={(value) => setForm((prev) => ({ ...prev, dateOfBirth: value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>{t.phone}</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
                />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>{t.email}</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                />
              </div>
            </div>

            <div className="flex items-start gap-2">
              <Checkbox
                id="member-representative"
                checked={form.isRepresentative}
                disabled={(externalRepresentative || flagCapReached) && !form.isRepresentative}
                onCheckedChange={(checked) =>
                  setForm((prev) => ({ ...prev, isRepresentative: checked === true }))
                }
              />
              <div>
                <Label htmlFor="member-representative">{t.representative}</Label>
                <p className="text-xs text-muted-foreground">
                  {externalRepresentative && !form.isRepresentative
                    ? t.representativeBlocked
                    : flagCapReached && !form.isRepresentative
                      ? t.representativeCap(MAX_CLIENT_REPRESENTATIVES)
                      : t.representativeHelp}
                </p>
              </div>
            </div>

            {/* QL-134 — what entitles this member to represent the group. Same
                vocabulary the Representative tab offers for external parties. */}
            {form.isRepresentative && (
              <div className="space-y-1">
                <Label>{t.authorityBasis}</Label>
                <Select
                  value={form.authorityBasis}
                  onValueChange={(v) => setForm((prev) => ({ ...prev, authorityBasis: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t.selectBasis} />
                  </SelectTrigger>
                  <SelectContent>
                    {CLIENT_PARTY_AUTHORITY_BASES.map((basis) => (
                      <SelectItem key={basis} value={basis}>
                        {t.basisLabels[basis] ?? basis}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <DialogCancel>{t.cancel}</DialogCancel>
            <Button onClick={handleSave} disabled={!isValid || !isDirty} loading={saving}>
              {editing ? t.save : t.addMember}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <VerifiedIdDialog
        open={identityOpen}
        onClose={() => setIdentityOpen(false)}
        verifications={identity?.verifications ?? []}
      />

      <ConfirmationDialog />
    </Card>
  );
}
